import React, { useState, useEffect, useCallback } from 'react';
import {
  FiPlus,
  FiEdit2,
  FiRefreshCw,
  FiToggleLeft,
  FiToggleRight,
  FiMap,
  FiSearch,
  FiDownload
} from 'react-icons/fi';
import { motion } from 'framer-motion';
import { AdminLayout } from '../../components/admin';
import { useAdminAuth } from '../../hooks/admin/useAdminAuth';
import { useErrorHandler } from '../../hooks/useErrorHandler';
import { useDialog } from '../../hooks/useDialog';
import { toast } from 'react-hot-toast';
import ProvinceFormModal from '../../components/admin/provinces/ProvinceFormModal';
import { Button } from '../../components/ui/Button';
import { Card, CardContent } from '../../components/ui/Card';
import { API_URL } from '../../services/api';
import { exportToExcel } from '../../utils/exportToExcel';

// Types
interface Province {
  id: string;
  nameTh: string;
  nameEn?: string;
  region?: string;
  displayOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface ProvincesResponse {
  items: Province[];
  total: number;
}

// Region labels
const REGION_LABELS: Record<string, string> = {
  'Central': 'ภาคกลาง',
  'Northern': 'ภาคเหนือ',
  'Northeastern': 'ภาคตะวันออกเฉียงเหนือ',
  'Eastern': 'ภาคตะวันออก',
  'Western': 'ภาคตะวันตก',
  'Southern': 'ภาคใต้'
};

const ProvincesPage: React.FC = () => {
  const { token } = useAdminAuth();
  const [errorState, { handleError, clearError }] = useErrorHandler();
  const { showConfirm, ConfirmDialog, AlertDialog } = useDialog();

  // State
  const [provinces, setProvinces] = useState<Province[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingProvince, setEditingProvince] = useState<Province | null>(null);

  // API call function
  const callApi = useCallback(async <T = any>(options: {
    method: 'GET' | 'POST' | 'PUT' | 'DELETE';
    endpoint: string;
    data?: any;
  }): Promise<{ success: boolean; data: T; message?: string }> => {
    if (!token) {
      throw new Error('ไม่พบ token สำหรับการยืนยันตัวตน');
    }

    
    const response = await fetch(`${API_URL}${options.endpoint}`, {
      method: options.method,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: options.data ? JSON.stringify(options.data) : undefined,
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.detail || result.message || `HTTP ${response.status}`);
    }

    return {
      success: true,
      data: result.data || result,
      message: result.message
    };
  }, [token]);

  // Load provinces
  const loadProvinces = useCallback(async () => {
    try {
      setLoading(true);
      clearError();

      const response = await callApi<ProvincesResponse>({
        method: 'GET',
        endpoint: '/api/admin/provinces/'
      });

      if (response.success) {
        setProvinces(response.data.items || []);
      }
    } catch (error) {
      handleError(error, { context: 'ไม่สามารถโหลดข้อมูลจังหวัดได้' });
    } finally {
      setLoading(false);
    }
  }, [callApi, handleError, clearError]);

  // Initial load
  useEffect(() => {
    if (token) {
      loadProvinces();
    }
  }, [loadProvinces, token]);

  // Handle toggle active
  const handleToggleActive = async (province: Province) => {
    try {
      const response = await callApi({
        method: 'POST',
        endpoint: `/api/admin/provinces/${province.id}/toggle-active`
      });

      if (response.success) {
        toast.success(province.isActive ? 'ปิดใช้งานเรียบร้อยแล้ว' : 'เปิดใช้งานเรียบร้อยแล้ว');
        loadProvinces();
      }
    } catch (error) {
      handleError(error, { context: 'ไม่สามารถเปลี่ยนสถานะได้' });
    }
  };

  // Handle edit
  const handleEdit = (province: Province) => {
    setEditingProvince(province);
    setShowModal(true);
  };

  // Handle add new
  const handleAddNew = () => {
    setEditingProvince(null);
    setShowModal(true);
  };

  // Handle modal close
  const handleModalClose = () => {
    setShowModal(false);
    setEditingProvince(null);
  };

  // Handle modal success
  const handleModalSuccess = () => {
    loadProvinces();
    handleModalClose();
  };

  // Handle export to Excel
  const handleExportExcel = () => {
    exportToExcel({
      filename: `provinces-${new Date().toISOString().split('T')[0]}`,
      sheetName: 'จังหวัด',
      columns: [
        { header: 'ลำดับ', key: 'displayOrder', width: 10 },
        { header: 'ชื่อภาษาไทย', key: 'nameTh', width: 25 },
        { header: 'ชื่อภาษาอังกฤษ', key: 'nameEn', width: 25 },
        { header: 'ภูมิภาค', key: 'regionTh', width: 20 },
        { header: 'สถานะ', key: 'statusText', width: 12 },
        { header: 'วันที่สร้าง', key: 'createdAt', width: 18 },
      ],
      data: filteredProvinces.map(prov => ({
        ...prov,
        regionTh: prov.region ? REGION_LABELS[prov.region] || prov.region : '-',
        statusText: prov.isActive ? 'เปิดใช้งาน' : 'ปิดใช้งาน',
        createdAt: new Date(prov.createdAt).toLocaleDateString('th-TH'),
      }))
    });

    toast.success('ส่งออกข้อมูลเรียบร้อยแล้ว');
  };

  // Filter provinces
  const filteredProvinces = provinces.filter((province) =>
    province.nameTh.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (province.nameEn && province.nameEn.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (province.region && REGION_LABELS[province.region]?.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <AdminLayout>
      <div className="p-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center">
                <FiMap className="mr-3 h-8 w-8 text-blue-600" />
                จัดการจังหวัด
              </h1>
              <p className="mt-1 text-sm text-gray-600">
                จัดการจังหวัดทั่วประเทศไทย (ทั้งหมด {provinces.length} จังหวัด)
              </p>
            </div>
            <div className="flex space-x-3">
              <Button
                variant="secondary"
                leftIcon={<FiDownload />}
                onClick={handleExportExcel}
                disabled={loading || provinces.length === 0}
              >
                Export Excel
              </Button>
              <Button
                variant="secondary"
                leftIcon={<FiRefreshCw />}
                onClick={() => loadProvinces()}
                disabled={loading}
              >
                รีเฟรช
              </Button>
              <Button
                variant="default"
                leftIcon={<FiPlus />}
                onClick={handleAddNew}
              >
                เพิ่มจังหวัด
              </Button>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {errorState.error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
            <p className="font-medium">เกิดข้อผิดพลาด</p>
            <p className="text-sm">{errorState.error.message}</p>
          </div>
        )}

        {/* Search */}
        <div className="mb-6">
          <div className="relative">
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="ค้นหาจังหวัด..."
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {/* Provinces Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <FiRefreshCw className="animate-spin h-8 w-8 text-blue-600" />
                <span className="ml-2 text-gray-600">กำลังโหลด...</span>
              </div>
            ) : filteredProvinces.length === 0 ? (
              <div className="text-center py-12">
                <FiMap className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">
                  {searchTerm ? 'ไม่พบจังหวัดที่ค้นหา' : 'ยังไม่มีจังหวัด'}
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  {searchTerm ? 'ลองเปลี่ยนคำค้นหา' : 'เริ่มต้นด้วยการเพิ่มจังหวัดใหม่'}
                </p>
                {!searchTerm && (
                  <Button
                    variant="default"
                    leftIcon={<FiPlus />}
                    onClick={handleAddNew}
                    className="mt-4"
                  >
                    เพิ่มจังหวัดแรก
                  </Button>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        ลำดับ
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        ชื่อจังหวัด
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        ภูมิภาค
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        สถานะ
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        การจัดการ
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredProvinces
                      .sort((a, b) => a.displayOrder - b.displayOrder)
                      .map((province, index) => (
                        <motion.tr
                          key={province.id}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.3, delay: index * 0.05 }}
                          className="hover:bg-gray-50"
                        >
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {province.displayOrder}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {province.nameTh}
                              </div>
                              {province.nameEn && (
                                <div className="text-sm text-gray-500">
                                  {province.nameEn}
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-500">
                              {province.region ? REGION_LABELS[province.region] || province.region : '-'}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                province.isActive
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-gray-100 text-gray-800'
                              }`}
                            >
                              {province.isActive ? 'เปิดใช้งาน' : 'ปิดใช้งาน'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex items-center justify-end space-x-2">
                              <button
                                onClick={() => handleToggleActive(province)}
                                className={`p-1 ${
                                  province.isActive
                                    ? 'text-yellow-600 hover:text-yellow-900'
                                    : 'text-green-600 hover:text-green-900'
                                }`}
                                title={province.isActive ? 'ปิดใช้งาน' : 'เปิดใช้งาน'}
                              >
                                {province.isActive ? (
                                  <FiToggleRight className="h-5 w-5" />
                                ) : (
                                  <FiToggleLeft className="h-5 w-5" />
                                )}
                              </button>
                              <button
                                onClick={() => handleEdit(province)}
                                className="text-blue-600 hover:text-blue-900 p-1"
                                title="แก้ไข"
                              >
                                <FiEdit2 className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </motion.tr>
                      ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Province Form Modal */}
        <ProvinceFormModal
          isOpen={showModal}
          onClose={handleModalClose}
          province={editingProvince}
          onSuccess={handleModalSuccess}
        />

        {/* Dialog Components */}
        {ConfirmDialog}
        {AlertDialog}
      </div>
    </AdminLayout>
  );
};

export default ProvincesPage;
